import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Table from "../../../Table/Table";
import { InputAdornment, TextField, Pagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import apiClient from "../../../../pages/keycloak/axiosInstance";

const AuditSection = () => {
  // State for table data and search query
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);

  // Fetch projectId from localStorage and department from URL
  const location = useLocation();
  const department = location.pathname.split("/").filter(Boolean).pop() || "";
  const projectId = localStorage.getItem("selectedProject");
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Table headers with Serial Number and document_link sorting disabled
  const tableHeaders = [
    { dbParam: "serial_no", displayName: "S.No" },
    { dbParam: "doc_name", displayName: "Document Name" },
    { dbParam: "doc_type", displayName: "Document Type" },
    { dbParam: "action", displayName: "Action" },
    { dbParam: "date", displayName: "Date" },
    { dbParam: "document_link", displayName: "Document Link", sortable: false }, // Disable sorting
    { dbParam: "remark", displayName: "Remark" },
  ];

  // State for sorting
  const [order, setOrder] = useState("desc"); // Default to descending
  const [orderBy, setOrderBy] = useState("date"); // Default sort by date

  // Fetch data from backend (only once per project & department)
  const fetchAuditLogs = async () => {
    if (!projectId || !department) {
      setError("Project ID or Department is missing");
      setTableData([]);
      return;
    }

    try {
      const response = await apiClient.get(`get_audit_logs`, {
        params: { projectId, department },
      });

      // Sort data by date in descending order (newest first)
      const sortedData = response.data.auditLogs.sort((a, b) => {
        const dateA = new Date(a.actionDate);
        const dateB = new Date(b.actionDate);
        return dateB - dateA; // Descending: newest first
      });

      // Map only the actual data (NO serial_no here!)
      const mappedData = sortedData.map((log) => ({
        doc_name: log.documentName,
        doc_type: log.documentType,
        action: log.action,
        date: (() => {
          const dateStr = log.actionDate.split("T")[0];
          const [year, month, day] = dateStr.split("-");
          return `${day}-${month}-${year}`;
        })(),
        document_link: log.url ? (
          <a
            href={log.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "blue", textDecoration: "underline" }}
          >
            Link
          </a>
        ) : (
          <span
            style={{ color: "gray", cursor: "not-allowed" }}
            title="File has been deleted"
          >
            File Deleted
          </span>
        ),
        remark: log.remark,
      }));

      setTableData(mappedData);
      setFilteredData(mappedData); // Initially, filtered = full data
      setError(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to fetch audit logs";
      setError(errorMessage);
      setTableData([]);
      setFilteredData([]);
    }
  };

  // Fetch logs only when projectId or department changes
  useEffect(() => {
    fetchAuditLogs();
  }, [projectId, department]); // Removed currentPage

  // Handle sorting (applies to full dataset)
  const handleSort = (column) => {
    if (column === "document_link") return; // Skip sorting for non-sortable column

    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[column] == null && b[column] == null) return 0;
      if (a[column] == null) return 1;
      if (b[column] == null) return -1;
      return a[column].toString().localeCompare(b[column].toString(), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * (isAsc ? 1 : -1);
    });

    setFilteredData(sortedData);
    setCurrentPage(1); // Reset to first page after sorting
  };

  // Handle search input change
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = tableData.filter((row) =>
      Object.values(row).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(query)
      )
    );

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page after filtering
  };

  // Pagination logic
  const itemsPerPage = 20;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add serial numbers based on current page and index
  const paginatedDataWithSerial = paginatedData.map((row, index) => ({
    ...row,
    serial_no: (currentPage - 1) * itemsPerPage + index + 1,
  }));

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* Main Heading */}
      <h1 className="text-2xl font-bold text-center mb-6">AUDIT AND LOGS</h1>

      {/* Error Message */}
      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Search Bar */}
      <div className="mb-6">
        <TextField
          type="text"
          placeholder="Search logs..."
          value={searchQuery}
          onChange={handleSearch}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: "1px solid #d1d5db",
              "&:hover": {
                borderColor: "#9ca3af",
              },
            },
          }}
        />
      </div>

      {/* Table */}
      <Table
        tableHeaders={tableHeaders}
        tableData={paginatedDataWithSerial}
        order={order}
        orderBy={orderBy}
        handleSort={handleSort}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="medium"
          />
        </div>
      )}
    </div>
  );
};

export default AuditSection;